import { useCallback } from 'react';
import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import FieldErrors from './FieldErrors';
import { TextField } from './TextField';

export type TextFormFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  onChange?: (userId?: string) => void;
  onBlur?: () => void;
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
  autoFocus?: boolean;
  placeholder?: string;
  readOnly?: boolean;
};

export const TextFormField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  onBlur,
  onChange,
  fieldPath,
  control,
  rules,
  autoFocus,
  placeholder,
  readOnly,
}: TextFormFieldProps<TFieldValues, TFieldPath>) => {
  const {
    field: { name, onBlur: innerOnBlur, onChange: innerOnChange, ref, value },
    fieldState: { invalid, error },
  } = useController({ name: fieldPath, control, rules });

  const composedOnBlur = useCallback(() => {
    innerOnBlur();
    if (onBlur) {
      onBlur();
    }
  }, [onBlur, innerOnBlur]);

  const composedOnChange = useCallback(
    (userId?: string) => {
      innerOnChange(userId);
      onChange?.(userId);
    },
    [onChange, innerOnChange],
  );

  return (
    <div>
      <TextField
        value={value}
        onChange={composedOnChange}
        ref={ref}
        name={name}
        onBlur={composedOnBlur}
        autoFocus={autoFocus}
        placeholder={placeholder}
        readOnly={readOnly}
      />
      {invalid && error && <FieldErrors error={error} />}
    </div>
  );
};
